/* eslint-disable no-console */

import { DialogProgrammatic as Dialog } from 'buefy'
import { consoleLogHeaderItem, chain } from './helpers'
import { humps } from './humps'
const jwt = require('jsonwebtoken')

const getRedirectUrl = function (that, redirectCookieName) {
  const locale = that.$i18n.locale
  const nextStep = that.$cookies.get(redirectCookieName)
  const localeRedirect = locale === undefined || locale === null || locale === 'en' ? '' : `${locale}`
  const appRedirect = nextStep === undefined || nextStep === null ? '/' : `/${nextStep}/`
  const fullRedirect = localeRedirect + appRedirect
  return fullRedirect
}

const getUserFromIdentity = function (identity) {
  const user = {
    email: identity.meta.email || '',
    sub: identity.entityUuid || '',
    name: identity.meta.userName,
    given_name: identity.meta.firstName || '',
    family_name: identity.meta.lastName || '',
    phone: identity.meta.phone || ''
  }

  return user
}

const state = () => ({
  loading: [],
  token: '',
  identity: undefined,
  refreshToken: '',
  ip: '',
  locale: 'en',
  devMode: false,
  supportedLanguages: [
    { code: 'en', text: 'English' },
    { code: 'es', text: 'Español' }
  ],
  hydrateActions: [],
  hydrateActionsAsync: [],
  logoutActions: [],
  redirectCookieName: undefined,
  magicTokenContext: {}
})

const mutations = {
  reset(state) {
    state.token = ''
    state.refreshToken = ''
    state.identity = {}
    state.ip = ''
    state.locale = 'en'
    state.loading = []
  },
  setMagicTokenContext(state, payload) {
    state.magicTokenContext = payload
  },
  setHydrateActions(state, payload) {
    state.hydrateActions = payload
  },
  setHydrateActionsAsync(state, payload) {
    state.hydrateActionsAsync = payload
  },
  setLogoutActions(state, payload) {
    state.logoutActions = payload
  },
  setRedirectCookieName(state, payload) {
    state.redirectCookieName = payload
  },
  setLocale(state, payload) {
    state.locale = payload
  },
  setLoading(state, payload) {
    state.loading.push(payload.key)

    // unique the array
    state.loading = [...new Set(state.loading)]

    // remove
    if (!payload.value) {
      state.loading = state.loading.filter((e) => e !== payload.key)
    }
  },
  setIdentity(state, payload) {
    let meta
    if (payload.meta) {
      meta = humps.camelizeKeys(payload.meta)
    }
    const camelCasedIdentity = humps.camelizeKeys(payload)
    camelCasedIdentity.meta = meta
    state.identity = camelCasedIdentity
  },
  setToken(state, encryptedToken) {
    state.token = encryptedToken
    if (state.token === null) {
      state.identity = {}
    }

    this.$axios.setHeader('Authorization', 'Bearer ' + encryptedToken)

    let cookieDomain = process.env.COOKIE_DOMAIN
    this.$cookies.set('auth._token.uptogether', encryptedToken, {
      path: '/',
      domain: cookieDomain,
      maxAge: 60 * 60 * 24 * 7
    })
  },
  setRefreshToken(state, encryptedToken) {
    state.refreshToken = encryptedToken

    let cookieDomain = process.env.COOKIE_DOMAIN
    this.$cookies.set('refresh._token.uptogether', encryptedToken, {
      path: '/',
      domain: cookieDomain,
      maxAge: 60 * 60 * 24 * 7
    })
  },
  setIpAddress(state, payload) {
    state.ip = payload
  },
  toggleDev(state) {
    state.devMode = !state.devMode
  }
}



const actions = {
  async logout(context, refreshPage=false) {
    try {
      context.commit('setLoading', { key: 'logout', value: true })

      // Remove auth related cookies
      const authCookies = [
        'auth._refresh_token.local',
        'auth._token.google',
        'auth._token.facebook',
        'auth._token.local',
        'auth._token.uptogether',
        'auth.strategy',
        'refresh._token.uptogether',
      ]

      authCookies.forEach(cookie => {
        this.$cookies.remove(cookie)
      });

      // log out of $auth service
      this.$auth.logout()

      context.commit('setToken', null)
      context.commit('setRefreshToken', null)

      const promises = []
      for (let i = 0; i < context.state.logoutActions.length; i++) {
        const element = context.state.logoutActions[i]
        promises.push(context.dispatch(element, {}, { root: true }))
      }
      await Promise.all(promises)

    } catch (error) {
      context.commit('setLoading', { key: 'logout', value: false })
      alert('logout failure')
    } finally {
      context.commit('setLoading', { key: 'logout', value: false })
    }

    // refresh current page to trigger middleware authentication
    if(refreshPage){
      this.$router.go()
    }
  },
  async _hydrateSession(context) {
    try {
      context.commit('setLoading', { key: 'login', value: true })

      // Fire off hydrateActionsAsync
      for (let i = 0; i < context.state.hydrateActionsAsync.length; i++) {
        const element = context.state.hydrateActionsAsync[i]
        context.dispatch(element, {}, { root: true })
      }

      // Fire off actions we need to wait for
      const promises = []
      for (let i = 0; i < context.state.hydrateActions.length; i++) {
        const element = context.state.hydrateActions[i]
        promises.push(context.dispatch(element, {}, { root: true }))
      }
      await Promise.all(promises)

      context.commit('setLoading', { key: 'login', value: false })
    } catch (error) {
      context.commit('setLoading', { key: 'login', value: false })
      console.log('hydrateSession failure', error)
      console.log('hydrateSession failure (response)', error.response)
      alert('hydrateSession failure')
    }
  },
  async _login(context, payload) {
    try {
      context.commit('setLoading', { key: 'login', value: true })

      const loginType = payload.loginType
      const loginObject = payload.loginObject

      const response = await this.$axios.$post('/AUTH_URL/user/login', loginObject)
      response.loginType = loginType
      await context.dispatch('globalLogin', response)
      await context.dispatch('_hydrateSession')
      context.commit('setLoading', { key: 'login', value: false })

      return response
    } catch (error) {
      context.commit('setLoading', { key: 'login', value: false })
      await context.dispatch('logout')
      Dialog.alert({
        title: this.app.i18n.t('login_error'),
        message: this.app.i18n.t('login_error_message'),
        confirmText: 'Ok'
      })
      this.$router.push({
        path: `${this.$i18n.locale === 'en' ? '' : '/' + this.$i18n.locale}/login`
      })
    }
  },
  async globalLogin(context, payload) {
    context.commit('setToken', payload.access_token)

    const decodedAccessToken = jwt.decode(payload.access_token)
    if (decodedAccessToken && decodedAccessToken.identity) {
      context.commit('setIdentity', decodedAccessToken.identity)
    }

    context.commit('setRefreshToken', payload.refresh_token)
    context.commit('setIpAddress', payload.ip_address)
  },
  async socialLogin(context) {
    try {
      const authStrategy = this.$cookies.get('auth.strategy')
      const authToken = this.$cookies.get(`auth._token.${authStrategy}`)

      if (!authStrategy || !authToken) {
        // logout
        await context.dispatch('logout')
        return
      }

      const tokenPayload = {
        loginType: 'social',
        loginObject: {
          strategy: authStrategy,
          token: authToken.replace('Bearer ', '')
        }
      }

      await context.dispatch('_login', tokenPayload)

      const fullRedirect = getRedirectUrl(this, context.state.redirectCookieName)
      this.$router.replace({ path: fullRedirect }).catch((err) => {
        console.log(`router replace error: ${err}`)
      })
    } catch (error) {
      consoleLogHeaderItem(`..error: ${error}`)
    }
  },
  simulateRefreshTokenFailure(context) {
    context.commit('setRefreshToken', undefined)
    context.dispatch('refreshToken')
  },
  async refreshToken(context, payload) {
    let force = false
    if (payload && payload.force) {
      force = true
    }

    // If not logged in, exit
    if (!this.$auth.loggedIn && !force) {
      console.log('Action [refreshToken]: user is not logged in.  Returning.')
      return
    }

    try {
      // This request allow us to refresh the user token when is required. It includes two headers:
      // - Authorization: The refresh token get during the auth setting or a previous refresh
      // - Cache-Control: Avoids caching from disk when the browser tries to (on back to page)
      const payload = await this.$axios.$get('/AUTH_URL/user/token/refresh?v=' + window.location.hostname, {
        headers: {
          'Authorization': 'Bearer ' + context.state.refreshToken,
          'Cache-Control': 'no-cache, no-store'
        }
      })
      payload.loginType = 'refresh'
      await context.dispatch('globalLogin', payload)
      return true
    } catch (error) {
      consoleLogHeaderItem(`refreshToken error: ${error}\n\n`)

      // Determine page, then send user to Login site.
      const loc = window.location.href
      const encodedLoc = encodeURIComponent(loc)
      let pushUrl = `${process.env.AUTH_URL}?redirect_url=${encodedLoc}&action=expired`
      window.location.href = pushUrl
      return false
    }
  },
  async createMagicToken(context, payload) {

    const redirectSlug = this.$cookies.get(context.state.redirectCookieName)
    payload.locale = this.$i18n.locale
    payload.redirectSlug = redirectSlug

    payload.context = context.state.magicTokenContext

    try {
      const apiResponse = await this.$axios.$post('/AUTH_URL/user/login/token/create', payload)
    } catch (error) {
      consoleLogHeaderItem(`error: ${error}`)
    }
  },
  async consumeMagicToken(context, magicToken) {

    try {
      const tokenPayload = {
        loginType: 'login_token',
        loginObject: {
          strategy: 'login_token',
          token_uuid: magicToken
        }
      }
      const response = await context.dispatch('_login', tokenPayload)

      const user = getUserFromIdentity(context.state.identity)
      this.$auth.setUser(user)

      // There are three redirect strategies:
      // 1.  Magic token provided redirect
      // 2.  RedirectCookie slug
      // 3.  Fallback to default redirect (nuxt auth handles this)

      // Strategy #1:  If the Token has been configured with a redirect url,
      // it's the top priority redirect.
      const tokenRedirectUrl = chain(context.state.identity, ['extras', 'loginTokenMeta', 'redirectUrl'])
      const tokenRedirectSlug = chain(context.state.identity, ['extras', 'loginTokenMeta', 'redirectSlug'])
      const tokenRedirectLocale = chain(context.state.identity, ['extras', 'loginTokenMeta', 'locale'])

      if (tokenRedirectUrl) {
        let finalRedirect

        let locale = tokenRedirectLocale && tokenRedirectLocale !== 'en' ? `/${tokenRedirectLocale}` : ''
        let slug = tokenRedirectSlug ? `/${tokenRedirectSlug}` : ''

        finalRedirect = `${tokenRedirectUrl}${locale}${slug}`

        // Replace the full url (causes page reload) if the tokenRedirectUrl contains "http"
        if (finalRedirect.includes("http")) {
          window.location.href = finalRedirect
        } else {
          this.$router.replace({ path: finalRedirect }).catch((err) => {
            console.log(`router replace error: ${err}`)
          })
        }
        return
      }

      // Strategy #2:  If the app has been configured with a redirect cookie, it holds
      // the slug value to redirect to.
      const fullRedirect = getRedirectUrl(this, context.state.redirectCookieName)

      this.$router.replace({ path: fullRedirect }).catch((err) => {
        console.log(`router replace error: ${err}`)
      })
      return true
    } catch (error) {
      consoleLogHeaderItem('consumeMagicToken error')
      consoleLogHeaderItem(error)
      consoleLogHeaderItem(error.response)
      return error.response
    }
  },
  async loginUsingExistingToken(context, refreshToken) {
    try {
      context.commit('setRefreshToken', refreshToken)

      // refreshToken calls globalLogin, which sets the identity
      const refreshTokenValid = await context.dispatch('refreshToken', { force: true })

      if (!refreshTokenValid) {
        // user must log in
        console.log('loginUsingExistingToken:  refreshToken is invalid.  Logging out the user.')
        await context.dispatch('logout')
        return
      }

      const user = getUserFromIdentity(context.state.identity)
      this.$auth.setUser(user)
      await context.dispatch('_hydrateSession')

      return context.state.identity
    } catch (error) {
      consoleLogHeaderItem('loginUsingExistingToken error')
      consoleLogHeaderItem(error)
      consoleLogHeaderItem(error.response)
      return error.response
    }
  },
  async updateUserProfile(context, tokenPayload) {
    try {
      context.commit('setLoading', { key: 'profile', value: true })
      // convert camelCased object keys into
      // snake_cased for db storage
      const snakeCasedPayload = humps.decamelizeKeys(tokenPayload)
      await this.$axios.$patch('/AUTH_URL/user', snakeCasedPayload)

      // refresh a token.  this will cause a profile update.
      await context.dispatch('refreshToken')
      Dialog.alert({
        title: 'Profile Updated',
        message: 'Your profile has been saved.',
        confirmText: 'Ok'
      })
    } catch (error) {
      console.log(error)
    } finally {
      context.commit('setLoading', { key: 'profile', value: false })
    }
  }
}

const getters = {
  loading(state) {
    return state.loading.length !== 0
  },
  loggedIn(state) {
    return state.identity ? true : false
  },
  isAdmin(state, getters) {
    try {
      const email = state.identity.meta.email
      if (!email) return false

      if (email && email.includes('@fii.org')) {
        return true
      }
    } catch (error) {
      return false
    }

    return false
  },
  devMode(state) {
    return state.devMode
  },
  supportedLanguages(state) {
    return state.supportedLanguages
  },
  username(state) {
    if (state.identity && state.identity.meta) {
      return state.identity.meta.userName || state.identity.meta.email || state.identity.meta.phone
    } else {
      return '--'
    }
  },
  identityMeta(state){
    if(state.identity && state.identity.meta){
      return state.identity.meta
    } else return {}
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
